<template>
  <div class="ak-btn-group" :class="classes">
    <slot />
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-button-group :vertical=false :ghost=false></ak-button-group>
   */
  name: 'AkButtonGroup',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Boolean}
     * @description This sets the "ghost" class of the component if
     * required, you will still need to apply the ghost property to
     * the buttons contained within, this just controls the styles
     * of the group itself
     */
    ghost: Boolean,

    /**
     * @type {Boolean}
     * @description This controls whether the component will render
     * horizontally or vertically
     */
    vertical: Boolean,
  },

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    const classes = computed(() => {
      const classes = []
      if (typeof props.ghost === 'boolean' && props.ghost) {
        classes.push('ak-btn-group--ghost')
      }
      if (typeof props.vertical === 'boolean' && props.vertical) {
        classes.push('ak-btn-group--vertical')
      }
      return classes
    })

    return {
      classes,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
@import '../../scss/_mixins/_custom';

.ak-btn-group {
  position: relative;
  display: inline-flex;
  border-radius: $base-border-radius;

  > * {
    position: relative;
    // Bring the "active" button to the front
    &:hover,
    &:focus,
    &:active,
    &.is-active {
      z-index: 2;
    }
  }

  * + * {
    margin-left: $base-border-width;
  }

  > *:not(:first-child):not(:last-child) {
    border-radius: 0;
  }

  > *:first-child {
    margin-left: 0;
    &:not(:last-child):not(.dropdown-toggle) {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  > *:last-child:not(:first-child),
  > .dropdown-toggle:not(:first-child) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  &.ak-btn-group--ghost {
    * + * {
      margin-left: (-$base-border-width);
      border-left: $base-border-width !important;
    }
  }
}
// On active and open, don't show outline
.ak-btn-group .dropdown-toggle:active,
.ak-btn-group.open .dropdown-toggle {
  outline: 0;
}

// Vertical button groups
// ----------------------

.ak-btn-group--vertical {
  flex-direction: column;

  > * {
    text-align: left;
    display: block;
    width: 100%;
    max-width: 100%;
    margin-top: $base-border-width;
    margin-left: 0;

    &:not(:first-child):not(:last-child) {
      border-radius: 0;
    }

    &:first-child:not(:last-child):not(.dropdown-toggle) {
      border-top-right-radius: $base-border-radius;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    &:last-child:not(:first-child):not(.dropdown-toggle) {
      border-bottom-left-radius: $base-border-radius;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  }

  // Center aligned button group
  // -------------------------
  &--center-aligned {
    > * {
      text-align: center;
    }
  }

  // Right aligned button group
  // -------------------------
  &--right-aligned {
    > * {
      text-align: right;
    }
  }
}
</style>
